import type { CmsEditField } from '@eneco/cms-adapter';

/** The attribute Visual Editing reads. */
export const DATA_SANITY = 'data-sanity';
/** Marks our own layout-neutral wrappers so {@link renderedElementOf} can see past them. */
export const ANCHOR = 'data-cms-edit-anchor';
/** Set by `EditMarker`: what kind of field the wrapped element renders, and how to recognise it. */
export const MARKER_KIND = 'data-cms-edit-marker';
export const MARKER_MATCH = 'data-cms-edit-match';

/**
 * The component's own outermost element that actually occupies space.
 *
 * Descends past every `display: contents` box, not only our own anchors. Ours are not the only
 * ones: `NbaFeedbackWrapperComponent` renders Sparky's `Unstyled` (`display: contents`) whenever a
 * block carries NBA feedback params, `TrackingProvider` renders it whenever `isEditMode` is false,
 * and any component may have a `contents` root of its own. Stopping at the first element would
 * then put the attribute back on a 0×0 box — the exact bug this is fixing.
 *
 * Tested by computed style rather than by a marker, because that is the property that decides
 * whether `getBoundingClientRect` returns anything, and it holds however the box got that way.
 */
export const renderedElementOf = (anchor: Element | null | undefined): Element | undefined => {
  let node = anchor?.firstElementChild ?? null;
  while (node && (node.hasAttribute(ANCHOR) || getComputedStyle(node).display === 'contents')) {
    node = node.firstElementChild;
  }
  return node ?? undefined;
};

export interface FieldMark {
  element: Element;
  /** Path to focus when this element is clicked. */
  path: string;
  /** The document that path is in, when it is not the page's own. */
  document?: CmsEditField['document'];
}

/** An `EditMarker` that has mounted somewhere below a block, however deep or far it was rendered. */
export interface RegisteredMarker {
  element: Element;
  kind: CmsEditField['kind'];
  match: string;
}

/**
 * The elements inside one block that render one of its {@link CmsEditField}s.
 *
 * **A field is left unmarked whenever it is ambiguous.** Two fields with one `match` — the same
 * asset shown twice, the same illustration on two tiles — cannot be told apart from the DOM, and
 * guessing would send an editor to the wrong control while looking correct. Falling back to the
 * block's own marker is slower for them but never misleading.
 *
 * Two ways of finding the element, because the two kinds differ in what they leave behind:
 *
 *  - An **image** is recognised by its `src` (or `srcset`) containing the asset's file name, which
 *    needs nothing from the component that rendered it. Only elements belonging to *this* block are
 *    considered — one whose nearest anchor is a child block's belongs to that block.
 *  - An **illustration** is an SVG drawn from a lookup key, with no identity in the DOM. It is found
 *    through the `EditMarker` it was wrapped in, and markers *register themselves* rather than being
 *    searched for. That is what makes ownership a fact about the React tree instead of the DOM, and it
 *    is the only thing that works for a marker rendered through a portal — a Radix dialog puts its
 *    content on `document.body`, nowhere near the block that owns it.
 */
export const locateEditFields = (
  anchor: Element,
  fields: readonly CmsEditField[] | undefined,
  markers: Iterable<RegisteredMarker>,
): FieldMark[] => {
  if (!fields || fields.length === 0) return [];

  const owns = (element: Element): boolean => element.closest(`[${ANCHOR}]`) === anchor;
  const registered = Array.from(markers);

  const occurrences = new Map<string, number>();
  for (const field of fields) {
    const id = `${field.kind}:${field.match}`;
    occurrences.set(id, (occurrences.get(id) ?? 0) + 1);
  }

  const marks: FieldMark[] = [];
  for (const field of fields) {
    if (occurrences.get(`${field.kind}:${field.match}`) !== 1) continue;
    const at = { path: field.path, ...(field.document ? { document: field.document } : {}) };

    if (field.kind === 'image') {
      for (const image of Array.from(anchor.querySelectorAll('img'))) {
        const sources = `${image.getAttribute('src') ?? ''} ${image.getAttribute('srcset') ?? ''}`;
        if (owns(image) && sources.includes(field.match)) marks.push({ element: image, ...at });
      }
      continue;
    }

    for (const marker of registered) {
      if (marker.kind !== field.kind || marker.match !== field.match) continue;
      const element = renderedElementOf(marker.element);
      if (element) marks.push({ element, ...at });
    }
  }
  return marks;
};
